import { motion } from "framer-motion";
import { Rocket, Wrench, LifeBuoy, Wallet, ArrowRight } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.14, duration: 0.8, ease: EASE } }),
};

const PILLARS = [
  { icon: Rocket, title: "A turn-key start", text: "Proven systems, ready-made materials, and step-by-step training — you don't need a legal background or sales experience to begin." },
  { icon: Wrench, title: "Back-office handled", text: "Billing, member support, and service delivery are run for you. Your job is conversations, not paperwork." },
  { icon: LifeBuoy, title: "Mentorship, not loneliness", text: "Work alongside experienced associates who coach you one-on-one. You build your own business, but never by yourself." },
  { icon: Wallet, title: "How you get paid", text: "The part everyone asks first: commissions on each membership you sell, plus renewal commissions while members stay active. The full pay structure is in writing before you start — no surprises." },
];

export default function Partner() {
  const prefillPartner = () => {
    window.dispatchEvent(new CustomEvent("lead-interest", { detail: "partner" }));
    scrollToId("#get-started");
  };

  return (
    <section id="partner" className="relative bg-[#0B192C] grain-overlay py-24 md:py-36 overflow-hidden scroll-mt-20" data-testid="partner-section">
      <div className="absolute inset-0 hero-spotlight" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-end mb-20">
          <div className="lg:col-span-8">
            <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0}
              className="font-mono-accent text-xs uppercase tracking-[0.28em] text-sky-400 mb-6">
              The partner opportunity
            </motion.p>
            <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={1}
              className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
              Build your own business helping people stop fearing the fine print.
            </motion.h2>
          </div>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={2}
            className="lg:col-span-4 text-slate-300 leading-relaxed">
            No products to stock. No inventory. You're connecting people and small businesses with
            legal protection they genuinely need — and you earn commissions on the memberships you
            personally sell, plus renewal commissions while those memberships stay active.
          </motion.p>
        </div>

        <motion.figure variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={2}
          className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-8 md:p-10 mb-14 flex items-start gap-6"
          data-testid="partner-quote">
          <img src="/john-jones.png" alt="John Jones" className="w-14 h-14 rounded-full object-cover border-2 border-sky-400/60 shrink-0" />
          <div>
            <blockquote className="font-display text-xl md:text-2xl text-white leading-snug">
              "I almost said no to this. Then I looked closer — a real system, real mentorship, and the pay structure in writing."
            </blockquote>
            <figcaption className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-sky-400 mt-4">— John, before he became an associate</figcaption>
          </div>
        </motion.figure>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i + 1}
              whileHover={{ y: -6 }}
              className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-8 md:p-10"
              data-testid={`partner-pillar-${i}`}
            >
              <span className="w-12 h-12 rounded-2xl bg-sky-400/15 text-sky-300 flex items-center justify-center mb-6">
                <p.icon size={22} strokeWidth={1.8} />
              </span>
              <h3 className="font-display font-bold text-xl text-white mb-3 tracking-tight">{p.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
          className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <button
            data-testid="partner-cta-button"
            onClick={prefillPartner}
            className="group flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-[#0B192C] font-semibold px-7 py-3.5 rounded-full transition-colors duration-300"
          >
            Ask me how partnering works
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p className="text-xs text-slate-500 max-w-md leading-relaxed" data-testid="partner-disclaimer">
            Straight talk, as it should be: this is a real business, not a shortcut to riches.
            Associates earn commissions only on memberships actually sold and renewed. Results vary
            with effort and consistency — there are no income guarantees, and no one should promise you any.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
